import { type JSX } from 'react';
import { Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SubscriptionStatus } from '../api/me.ts';

/**
 * Shown in place of a practice's content when it is PLAN-ONLY: its plan is cancelled or
 * suspended (owner 2026-08-28), or its free period ended 14+ days ago while it held a billable
 * case and no paid plan (owner 2026-09-12; ToS 9.5, terms-2.4) — the whole practice is
 * plan-only for every member either way. The server independently answers 402
 * SUBSCRIPTION_RESTRICTED for every route bound to the practice except its plan and terms
 * routes, so this mirrors a decision the API already enforces and never grants access the
 * server denies. The member's other practices are untouched.
 */
export function SubscriptionInterstitial({
  status,
  practiceId,
  practiceName,
  canManagePlan,
  canExport,
}: {
  /** 'pilot'/'trialing' here means the LADDER triggered this, not the Stripe status itself. */
  status: SubscriptionStatus | null;
  practiceId: string;
  practiceName: string;
  /** Back-office authority (owner implicit): the plan page is theirs to act on. */
  canManagePlan: boolean;
  /** `practice.govern` — the export screen refuses anyone else, so only they are told of it. */
  canExport: boolean;
}): JSX.Element {
  const heading =
    status === 'suspended' ? `${practiceName} is suspended`
      : status === 'cancelled' ? `${practiceName}'s plan has ended`
        : `${practiceName}'s free period has ended`;

  return (
    <div className="flex flex-1 items-center justify-center bg-background px-4">
      <div
        role="alert"
        data-testid="subscription-interstitial"
        className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-center shadow-lift"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <Lock className="h-6 w-6 text-destructive" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-semibold tracking-tight">{heading}</h1>
        {/* DERIVED, not written: the reach differs per viewer, and stating it as a universal
            has now been wrong in both directions — first "only the Plan page" while the export
            screen was promised, then "everyone" while the export screen refuses all but an
            owner. Each clause is shown only to someone it is true of. */}
        <p className="text-sm text-muted-foreground">
          Everything at this practice is kept.
          {canManagePlan ? ' You can still reach its Plan page' : ' Its Plan page stays open to whoever manages the plan'}
          {canExport ? ', and export the practice’s data from Compliance' : ''}
          {' '}until a plan is in place again. Your other practices are unaffected.
        </p>
        {/* Offer only what the viewer can use: a practitioner would be refused by every
            plan endpoint, so they are told whom to ask instead. */}
        <div className="flex flex-col items-center gap-2 pt-1">
          {canManagePlan ? (
            <Link
              to={`/practices/${practiceId}/plan`}
              className="text-sm font-medium underline underline-offset-2"
              data-testid="subscription-interstitial-plan-link"
            >
              Go to the plan page
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="subscription-interstitial-ask">
              Ask the practice owner to pick a plan.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
